/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import {useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {compareBy, Order, stableSort} from "../../helpers/TableHelpers";
import {FilterCombobox} from "../common/FilterCombobox";
import SortableTableColumnHeader from "../common/SortableTableColumnHeader";
import JobRequestStatusChip from "../Jobs/JobRequestStatusChip";
import {JobRequestStatus, JobRequestStatusColors, JobRequestStatusIcons, JobRequestStatusLabels,} from "../../models/Jobs/JobRequestStatus";
import {ExportCasesToCsvRequest, JobRequest,} from "../../models/Jobs/JobRequest";
import AbortJobModal from "../Jobs/AbortJobModal";
import JobsRepository from "../../repositories/JobsRepository";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  drawerFiltersBoxClassName,
  drawerFilterItemClassName,
  drawerTableWrapperClassName,
} from "../common/Table/TableStyles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Trash2, Download, XCircle } from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface ExportCasesToCsvStatusTableProps {
  organizationId: string;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const ExportCasesToCsvStatusTable: React.FC<ExportCasesToCsvStatusTableProps> = ({
  organizationId,
  userRole,
  isAssignedUser
}: ExportCasesToCsvStatusTableProps) => {
  const requiresReason = useRequiresReason();
  const [order, setOrder] = React.useState<Order>("desc");
  const [sort, setSort] = React.useState<keyof ExportCasesToCsvRequest>("exportName");
  const [statusFilter, setStatusFilter] = React.useState<string | null>(null);
  const [abortJob, setAbortJob] = useState<JobRequest | null>(null);
  const [exportCasesStatus, setExportCasesStatus] = useState<ExportCasesToCsvRequest[] | null>(null);
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();
  const casesQuery = useQuery({ queryKey: [QUERY_KEYS.EXPORT_CASES, organizationId], queryFn:
    async () => {
      return await jobsRepository.getCasesCsvGenerationStatus(organizationId);
    }, staleTime: 5 * 60 * 1000,
      refetchInterval: 5 * 60 * 1000 + 1,
      refetchIntervalInBackground: true,
      refetchOnMount: true, });

  useEffect(() => {
    if (casesQuery.data) {
      setExportCasesStatus(casesQuery.data);
    }
  }, [casesQuery]);

  const handleDownload = (url: string | undefined) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const changeSort = (newSort: keyof ExportCasesToCsvRequest) => {
    setSort(newSort);
  };

  const changeOrder = (newOrder: Order) => {
    setOrder(newOrder);
  };

  const { isError, isFetching } = casesQuery;

  const filteredJobs = (exportCasesStatus || []).filter((job) => {
    return statusFilter == null || job.status === statusFilter;
  });

  const sortedJobs = sort
    ? stableSort<ExportCasesToCsvRequest>(filteredJobs, compareBy(order, sort))
    : filteredJobs;

  return (
    <div className="flex flex-col w-full flex-1 min-h-0">
      <div className={drawerFiltersBoxClassName}>
        <div className={drawerFilterItemClassName}>
          <Label className="text-sm">Status</Label>
          <FilterCombobox
            value={statusFilter}
            onChange={(newValue) => {
              setStatusFilter(newValue);
            }}
            placeholder="Status"
            options={Object.values(JobRequestStatus)
              .filter((status) => status !== JobRequestStatus.New)
              .map((status) => ({
                value: status as string,
                label: JobRequestStatusLabels[status as JobRequestStatus],
                color: JobRequestStatusColors[status as JobRequestStatus],
                icon: JobRequestStatusIcons[status as JobRequestStatus],
              }))}
          />
        </div>
      </div>

      <div
        className={cn(drawerTableWrapperClassName, (isFetching || isError) && "opacity-50 pointer-events-none")}
      >
        <Table className="table-fixed" containerClassName="flex-1 min-h-0">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[15%] min-w-[48px] text-center" />

                <TableHead className="w-[40%]">
                  <SortableTableColumnHeader
                    label={"Name"}
                    sortProperty={"exportName"}
                    getSort={() => sort}
                    setSort={(newSort) =>
                      changeSort(newSort as keyof ExportCasesToCsvRequest)
                    }
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>

                <TableHead className="w-[30%]">
                  <SortableTableColumnHeader
                    label={"Status"}
                    sortProperty={"status"}
                    getSort={() => sort}
                    setSort={(newSort) =>
                      changeSort(newSort as keyof JobRequest)
                    }
                    getOrder={() => order}
                    setOrder={(newOrder) => changeOrder(newOrder)}
                  />
                </TableHead>
                <TableHead className="w-[15%]" />
              </TableRow>
            </TableHeader>

            <TableBody>
              {sortedJobs.map((exportCsvJob) => {
                const canEdit =
                  exportCsvJob.status === JobRequestStatus.Pending ||
                  exportCsvJob.status === JobRequestStatus.Completed;
                return (
                  <TableRow key={exportCsvJob.id}>
                    <TableCell>
                      {requiresReason ? (
                        <ActionConfirmationModal
                          actionConfig={ACTION_CONFIGS.EXPORT_CASE}
                          onConfirm={async (reason) => {
                            try {
                              await jobsRepository.auditLogCasesCsvExportDownload(exportCsvJob.id, reason);
                              handleDownload(exportCsvJob.exportedCsvUrl);
                            } catch (error) {
                              throw error;
                            }
                          }}
                        >
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            aria-label="Download CSV"
                            className="text-primary"
                            disabled={!exportCsvJob.exportedCsvUrl}
                          >
                            <Download className="size-4" />
                          </Button>
                        </ActionConfirmationModal>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Download CSV"
                          className="text-primary"
                          onClick={() =>
                            handleDownload(exportCsvJob.exportedCsvUrl)
                          }
                          disabled={!exportCsvJob.exportedCsvUrl}
                        >
                          <Download className="size-4" />
                        </Button>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm truncate block">
                        {exportCsvJob.exportName}
                      </span>
                    </TableCell>
                    <TableCell>
                      <JobRequestStatusChip status={exportCsvJob.status} />
                    </TableCell>
                    {canEdit ? (
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          aria-label="Cancel"
                          className="text-destructive"
                          onClick={() => {
                            setAbortJob(exportCsvJob);
                          }}
                          disabled={!isAssignedUser && userRole !== UserRole.Admin}
                        >
                          {exportCsvJob.status ===
                          JobRequestStatus.Completed ? (
                            <Trash2 className="size-4" />
                          ) : (
                            <XCircle className="size-4" />
                          )}
                        </Button>
                      </TableCell>
                    ) : (
                      <TableCell />
                    )}
                  </TableRow>
                );
              })}
              {sortedJobs.length === 0 && !isFetching && (
                <TableRow>
                  <TableCell colSpan={99} className="h-24 text-center text-muted-foreground">
                    No exports.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
      </div>
      {abortJob ? (
        <AbortJobModal
          open={abortJob !== null}
          jobRequest={abortJob}
          onClose={() => {
            setAbortJob(null);
          }}
          onConfirm={async () => {
            await jobsRepository.deleteJob(abortJob.id);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_CASES, organizationId] });
            setAbortJob(null);
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default ExportCasesToCsvStatusTable;
